import {
    StreamRequestInfo,
    WidgetCustomManagedRestOptions,
    WidgetCustomStreamManager,
    WidgetRestByRequestUidConfig,
} from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import State from 'store/State';
import RequestProgressService from 'requestProgress/RequestProgressService';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import IndigoApiError from 'communication/IndigoApiError';
import { WidgetRequestResponseByUidRestManager } from 'widgets/streaming/widgetStreamManager/RequestResponse/WidgetRequestResponseByUidRestManager';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';

import {
    addError,
    addRequest,
    addResponse,
    clearData,
} from './store/data/dataSlice';

import { DataServices } from 'store/Services.ts';
import {
    IndexEventsRequest,
    toUid,
} from 'widgets/indexEvents/api/IndexEventsRequest';
import { IndexEventsResponse } from 'widgets/indexEvents/api/IndexEventsResponse';
import { callIndexEventsApi } from 'widgets/indexEvents/api/IndexEventsApi';
import { WidgetId } from 'widgets/WidgetId';
import { PortfolioSelection } from 'portfolio/PortfolioSelection';
import { selectWidgetInstanceConfig } from 'widgets/store/selectors';
import {
    IndexEventsWidgetConfigState,
    initialIndexEventsConfiguration,
} from './store/config/IndexEventsWidgetConfigState';

export const indexEventsStreamConfig: WidgetRestByRequestUidConfig<
    IndexEventsRequest,
    IndexEventsResponse
> = {
    apiUid: 'IndexEvents',
    managerFactory(
        options: WidgetCustomManagedRestOptions
    ): WidgetCustomStreamManager {
        return new WidgetRequestResponseByUidRestManager(options);
    },
    getWidgetStreamRequestInfo(
        state: State,
        _services: DataServices,
        id: WidgetId,
        _activeInputUsesOrdersStream: boolean
    ): Promise<
        | StreamRequestInfo<IndexEventsRequest>[]
        | StreamRequestInfo<IndexEventsRequest>
        | undefined
    > {
        return Promise.resolve(getIndexEventsStreamRequestInfo(state, id));
    },
    getStreamRequestInfosForSymbols(): Promise<
        StreamRequestInfo<IndexEventsRequest>[]
    > {
        return Promise.resolve([]);
    },
    callApi(
        dispatcher: ActionsDispatcher,
        requestProgressService: RequestProgressService,
        indigoServiceClient: IndigoServiceClient,
        _dataServices: DataServices,
        payload: IndexEventsRequest
    ): Promise<IndexEventsResponse> {
        return callApi(
            dispatcher,
            requestProgressService,
            indigoServiceClient,
            payload
        );
    },
    clearRequestData(): void {
        return;
    },
    handleMessage(
        dispatcher: ActionsDispatcher,
        key: string,
        request: IndexEventsRequest,
        response: IndexEventsResponse
    ): void {
        dispatcher.dispatch(addResponse({ request, response }));
    },
    handleError(
        dispatcher: ActionsDispatcher,
        key: string,
        request: IndexEventsRequest,
        error: IndigoApiError
    ): void {
        dispatcher.dispatch(addError({ request, error }));
    },
    removeRequestData(dispatcher: ActionsDispatcher, key: string): void {
        dispatcher.dispatch(clearData(key));
    },
};

function getIndexEventsStreamRequestInfo(
    state: State,
    id: WidgetId
): StreamRequestInfo<IndexEventsRequest> | undefined {
    const selection = selectPortfolioSelection(state);
    const config =
        selectWidgetInstanceConfig(state, id) ??
        initialIndexEventsConfiguration;
    return getIndexEventsStreamRequestInfoForPortfolioSelectionAndWidgetConfig(
        selection,
        config
    );
}

export function getIndexEventsStreamRequestInfoForPortfolioSelectionAndWidgetConfig(
    selection: PortfolioSelection | undefined,
    config: IndexEventsWidgetConfigState
) {
    const selectionWrapper = new PortfolioSelectionWrapper(selection);

    const symbols = selectionWrapper.selectedSymbols;
    const symbology = selectionWrapper.symbology;

    if (!symbols || symbols.length === 0) {
        return undefined;
    }

    const { side, tradeCategories: tradecategories } = config;

    const request: IndexEventsRequest = {
        symbols: symbols.join(','),
        symbology,
        side: side && side.length > 0 ? side : undefined,
        tradeCategories:
            tradecategories?.length > 0 ? tradecategories.join(',') : undefined,
    };

    const uid = toUid(request);

    return {
        streamKey: uid,
        payloadUid: uid,
        payload: request,
    };
}

async function callApi(
    dispatcher: ActionsDispatcher,
    requestProgressService: RequestProgressService,
    indigoServiceClient: IndigoServiceClient,
    request: IndexEventsRequest
) {
    dispatcher.dispatch(addRequest({ request }));
    dispatcher.dispose(); // Force delayed dispatch...

    return requestProgressService.invokeAndMonitorRequest(
        'callIndexEventsApi',
        (options) => callIndexEventsApi(indigoServiceClient, request, options)
    );
}
